import WakeWordService from './WakeWordService';
import OpenAIAssistant from './OpenAIAssistant.js';
import MediaKeyHandler from './MediaKeyHandler';
import { ToastContainer, toast } from 'react-toastify';
import { colorPaletteTool, additionTool } from './SampleTools';
import {
  createStopListeningTool,
  createActivateClinicModeTool,
  createActivateConversationModeTool,
} from './BuiltinTools';
import {
  FAKE_PRIMER,
  SYSTEM_DIRECTIVE_ULTRA,
  SYSTEM_DIRECTIVE_MEDIUM,
  SYSTEM_DIRECTIVE_NORMAL,
} from './Prompts';
import getDeviceData, {
  getDeviceFingerprint,
  detectOSType,
} from '../../../api/DeviceInfo';
import { isDemoMode } from '../../../utils/demoStorage';

export const VAStates = {
  INACTIVE: 'INACTIVE',
  WAKE_WORD: 'WAKE_WORD',
  ASSISTANT: 'ASSISTANT',
};

// Silence timeouts for each mode
const MODE_TIMEOUTS = {
  clinic: 100, // Quick return to wake word
  conversation: 5000, // Allow back-and-forth
};

// How long we keep the Realtime conversation history alive with no interaction.
// After this duration (while idle in wake-word state), we fully restart Synapse.
const MEMORY_TTL_MS = 5 * 60 * 1000; // 5 minutes

class VoiceAssistant {
  constructor(
    apiClient,
    onStateChange = null,
    deviceId = null,
    onCommandSuccess = null,
    mode = 'clinic',
    onModeChange = null,
    verbosity = 'medium',
    onVerbosityChange = null,
  ) {
    this.state = VAStates.INACTIVE;
    this.apiClient = apiClient;
    this.wakeWords = ['comprehend'];
    this.onStateChange = onStateChange;
    this.deviceId = deviceId;
    this.onCommandSuccess = onCommandSuccess;
    this.mode = mode;
    this.onModeChange = onModeChange;
    this.verbosity = verbosity;
    this.onVerbosityChange = onVerbosityChange;

    // Track user interaction to support inactivity-based memory reset
    this.lastInteractionAt = Date.now();
    this.memoryTimer = null;
    this.hasBeenUsedSinceReset = false; // Only reset if actually used

    // Pre-create audio elements for iOS Safari compatibility
    // iOS requires audio to be "unlocked" via user gesture before programmatic playback
    this.blipUpAudio = new Audio('/blip.mp3');
    this.blipDownAudio = new Audio('/blip_down.mp3');
    this.blipUpAudio.preload = 'auto';
    this.blipDownAudio.preload = 'auto';
    // Pre-load the audio files
    this.blipUpAudio.load();
    this.blipDownAudio.load();
    this.audioUnlocked = false;

    // Media key handler for Play/Pause button
    this.mediaKeyHandler = new MediaKeyHandler();

    // We must bind the method to THIS (VoiceAssistant) class instance otherwise it will use the caller as it's "this"
    this.wakeWordCallback = this.wakeWordCallback.bind(this);
    this.silenceDetected = this.silenceDetected.bind(this);
    this.stopListeningTool = this.stopListeningTool.bind(this);
    this.switchModeTool = this.switchModeTool.bind(this);
    this.handleMediaKeyPress = this.handleMediaKeyPress.bind(this);
    this.setVerbosity = this.setVerbosity.bind(this);
    const keywordUrl = '/Hey-Comprehend_en_wasm_v3_0_0.ppn';
    this.transcriptionService = new WakeWordService({
      // Access key is injected at runtime from the backend in startSession()
      accessKey: null,
      keywordModel: keywordUrl,
      sensitivity: 0.6,
      onWakeWord: this.wakeWordCallback,
      silenceCallback: this.silenceDetected,
      silenceTimeout: MODE_TIMEOUTS[this.mode] || MODE_TIMEOUTS.clinic,
      porcupineModelPublicPath: '/porcupine_params.pv',
      deviceId: this.deviceId,
    });
    this.AI = new OpenAIAssistant(
      this.transcriptionService,
      this.apiClient,
      this.deviceId,
      this.onCommandSuccess,
    );

    // Apply initial verbosity to the AI system directive
    this.applyVerbosityToAI();

    // Register the tool magic with the API client
    this.apiClient.ai_active = true;
    this.apiClient.addTools = this.AI.addTools;
    this.apiClient.removeTools = this.AI.removeTools;
    if (this.apiClient.pendingTools) {
      this.AI.addTools(this.apiClient.pendingTools);
    }

    // Register context/memory management with the API client
    this.apiClient.addContext = this.AI.addContext;
    this.apiClient.removeContext = this.AI.removeContext;
    if (
      this.apiClient.pendingContexts &&
      this.apiClient.pendingContexts.size > 0
    ) {
      this.apiClient.pendingContexts.forEach((contextData, contextId) => {
        this.AI.addContext(contextId, contextData);
      });
    }
  }

  setState(newState) {
    this.state = newState;
    if (this.onStateChange) {
      this.onStateChange(newState);
    }
  }

  /**
   * Internal helper to map the current verbosity to a system directive
   * and push it into the OpenAIAssistant (including live session.update).
   */
  applyVerbosityToAI() {
    if (!this.AI) {
      return;
    }
    const directives = {
      ultra: SYSTEM_DIRECTIVE_ULTRA,
      medium: SYSTEM_DIRECTIVE_MEDIUM,
      normal: SYSTEM_DIRECTIVE_NORMAL,
    };
    const directive = directives[this.verbosity] || SYSTEM_DIRECTIVE_MEDIUM;
    if (typeof this.AI.setSystemDirective === 'function') {
      this.AI.setSystemDirective(directive);
    } else {
      // Fallback – should not happen, but keeps things safe
      this.AI.SYSTEM_DIRECTIVE = directive;
    }
  }

  /**
   * Public API for the UI to change verbosity mode.
   * Only user actions should call this; the AI itself does NOT change verbosity.
   * @param {'ultra'|'medium'|'normal'} newVerbosity
   */
  setVerbosity(newVerbosity) {
    if (!['ultra', 'medium', 'normal'].includes(newVerbosity)) {
      console.warn(`Invalid verbosity: ${newVerbosity}`);
      return;
    }
    if (newVerbosity === this.verbosity) {
      return;
    }
    this.verbosity = newVerbosity;
    this.applyVerbosityToAI();

    if (this.onVerbosityChange) {
      this.onVerbosityChange(newVerbosity);
    }
  }

  /**
   * Public API for the UI to change synapse mode (clinic vs conversation).
   * This delegates to switchModeTool which handles all the internal updates.
   * @param {'clinic'|'conversation'} newMode
   */
  setMode(newMode) {
    this.switchModeTool(newMode);
  }

  /**
   * Stop the conversation and return to wake word listening state
   * @param {string} reason - The reason for stopping (for logging/toast)
   * @param {string} toastMessage - Optional custom toast message
   */
  stopConversation(reason = 'unknown', toastMessage = null) {
    if (this.state === VAStates.ASSISTANT) {
      console.log(`Stopping conversation: ${reason}`);
      this.playDing(false);
      this.setState(VAStates.WAKE_WORD);
      this.lastInteractionAt = Date.now();

      // Cancel any in-progress AI response to interrupt it if speaking
      this.AI.cancelResponse();
      this.AI.stopListening();

      if (toastMessage) {
        toast.info(toastMessage, { autoClose: 1000 });
      }
    }
  }

  /**
   * Handle media key press (play/pause button from Ray-Ban glasses or keyboard shortcuts)
   * Acts as a toggle: starts wake word OR stops conversation depending on current state
   */
  handleMediaKeyPress(source) {
    console.log(`Media key pressed from: ${source}`);

    if (this.state === VAStates.ASSISTANT) {
      // If we're in conversation mode, stop the conversation
      this.stopConversation('media key', '🛑 Conversation stopped');
    } else if (this.state === VAStates.WAKE_WORD) {
      // If we're listening for wake word, trigger it manually
      console.log('Triggering wake word via media key');
      const sourceEmoji =
        {
          play: '▶️',
          pause: '⏸️',
          'keyboard-shortcut': '⌨️',
          space: '🎙️',
        }[source] || '🎵';
      toast.info(`${sourceEmoji} Manual trigger`, { autoClose: 1000 });

      // wakeWordCallback will handle pausing silence detection
      this.wakeWordCallback();
    } else {
      console.log('Media key pressed but assistant is not active');
    }
  }

  async startSession() {
    console.log('Starting voice assistant...');

    // Unlock audio for iOS Safari - must happen during user gesture
    // Play muted to "unlock" the audio elements for later programmatic playback
    if (!this.audioUnlocked) {
      try {
        // Use muted property (more reliable than volume=0 on iOS)
        this.blipUpAudio.muted = true;
        this.blipDownAudio.muted = true;

        // Play to unlock - must happen during user gesture
        const playPromises = [
          this.blipUpAudio.play().catch(() => {}),
          this.blipDownAudio.play().catch(() => {}),
        ];
        await Promise.all(playPromises);

        // Small delay to ensure iOS registers the play
        await new Promise((r) => setTimeout(r, 50));

        // Pause and reset for actual use later
        this.blipUpAudio.pause();
        this.blipUpAudio.currentTime = 0;
        this.blipUpAudio.muted = false;
        this.blipDownAudio.pause();
        this.blipDownAudio.currentTime = 0;
        this.blipDownAudio.muted = false;

        this.audioUnlocked = true;
        console.log('🔊 Audio unlocked for iOS');
      } catch (e) {
        console.warn('Failed to unlock audio:', e);
      }
    }

    // Fetch the Picovoice wakeword key and OpenAI realtime ephemeral key in parallel
    try {
      // Collect device info for usage tracking (Picovoice bills per device)
      const deviceFingerprint = getDeviceFingerprint();
      const deviceData = await getDeviceData();
      const deviceInfoPayload = {
        deviceFingerprint,
        deviceInfo: {
          osType: deviceData.osType,
          browser: deviceData.browser,
          userAgent: deviceData.userAgent,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
        },
      };

      const [picovoiceKeyResponse, openAIKeyResponse] = await Promise.all([
        this.apiClient.getSynapseKey('picovoice_wakeword', deviceInfoPayload),
        this.apiClient.getSynapseKey('openai_realtime', deviceInfoPayload),
      ]);

      if (!picovoiceKeyResponse || !picovoiceKeyResponse.accessKey) {
        console.error(
          'Failed to obtain Picovoice access key',
          picovoiceKeyResponse,
        );
        toast.error('Failed to start wake word listener (no key)');
        return;
      }
      if (!openAIKeyResponse || !openAIKeyResponse.accessKey) {
        console.error(
          'Failed to obtain OpenAI ephemeral key',
          openAIKeyResponse,
        );
        toast.error('Failed to initialize AI assistant (no key)');
        return;
      }

      // Inject keys into the respective services
      this.transcriptionService.accessKey = picovoiceKeyResponse.accessKey;
      this.AI.ephemeralKey = openAIKeyResponse.accessKey;
    } catch (error) {
      console.error(
        'Error fetching Picovoice/OpenAI keys from backend in parallel:',
        error,
      );
      toast.error('Failed to start voice assistant (key fetch error)');
      return;
    }

    await this.transcriptionService.init();
    await this.AI.init();
    // Add the stop listening tool to the assistant targeting the local stop tool
    this.AI.addTools(createStopListeningTool(this.stopListeningTool));
    // Add the mode switch tool for the OTHER mode (so AI knows what it can switch to)
    this.registerModeToolForCurrentMode();

    // Start media key handler (captures Play/Pause from Ray-Ban glasses + keyboard shortcuts)
    this.mediaKeyHandler.start(this.handleMediaKeyPress);

    console.log('🎙️ Wake word triggers enabled:');
    console.log('  1. Say "comprehend" (voice)');
    console.log(
      '  2. Tap Ray-Ban temple / Press Play/Pause (toggle start/stop)',
    );

    // Set the state once initialized...
    this.setState(VAStates.WAKE_WORD);
    this.lastInteractionAt = Date.now();
    this.startMemoryWatcher();
    toast.info('Comprehend Voice Ready!');
  }

  stopSession() {
    // Prevent multiple stop calls
    if (this.state === VAStates.INACTIVE) {
      return;
    }

    this.setState(VAStates.INACTIVE);
    console.log('Stopping voice assistant...');
    this.AI.stopSession();
    this.transcriptionService.stop();

    // Stop media key handler
    this.mediaKeyHandler.stop();

    // Stop inactivity watcher
    this.stopMemoryWatcher();
  }

  wakeWordCallback() {
    console.log('got wake word:');
    this.lastInteractionAt = Date.now();
    this.hasBeenUsedSinceReset = true; // Mark session as used
    this.apiClient.transcript = this.transcriptionService.getTranscript();
    // Start the assistant with the shared track
    if (
      this.state === VAStates.WAKE_WORD &&
      this.transcriptionService.getMediaStream()
    ) {
      // Pause silence detection to give user time to speak their command
      // After 5s grace period, silence detection resumes with the configured timeout
      this.transcriptionService.pauseSilenceDetection(5000);

      // toast.info('Starting Assistant'); # Sticky Toast when view changes...
      this.playDing();
      this.setState(VAStates.ASSISTANT);
      // Adjust your usage of OpenAIAssistant as needed
      // Wake word takes ~2 seconds to call, so we need to feed the assistant a few seconds of audio prior to the wake word
      this.AI.startListening().then(() => {
        // If waiting for the ding is unacceptable, we can prepend the transcript as text
        // toast.info('Assistant started listening');
        // console.log('Assistant started listening');
        // // Throw a text message to the AI using the streaming transcription who is capturing everything after the wake word
        // // There is some latency between when the transcription is said and aws brings it back.
        // new Promise((resolve) => setTimeout(resolve, 1000)).then(() => {
        //   const preWake =
        //     this.transcriptionService.getTranscriptSinceWakeWord();
        //   console.log(`Pre-wake text: ${preWake}`);
        //   this.AI.sendTextMessage(
        //     `This was the beginning of the question being asked: ${preWake}`,
        //   );
        // });
        // Context: only inject FAKE_PRIMER when demo mode is enabled via localStorage
        if (isDemoMode()) {
          const context = FAKE_PRIMER;
          this.AI.sendTextMessage(
            `Here's some background context that may be useful: ${context}`,
          );
        }
        // this.AI.addTools([colorPaletteTool, additionTool]);
      });
    }
  }

  /**
   * Periodically checks for inactivity while idle in wake-word state and
   * resets the Realtime session (clearing conversation memory) after TTL.
   */
  startMemoryWatcher() {
    if (this.memoryTimer) {
      return;
    }
    this.memoryTimer = setInterval(() => {
      // Only consider resetting when we're idle between commands
      if (this.state !== VAStates.WAKE_WORD) {
        return;
      }
      if (!this.AI || !this.AI.isSessionActive) {
        return;
      }
      // Don't reset if the session hasn't been used since last reset
      if (!this.hasBeenUsedSinceReset) {
        return;
      }
      const now = Date.now();
      if (now - this.lastInteractionAt > MEMORY_TTL_MS) {
        this.resetConversationMemory('inactivity').catch((err) => {
          console.error('Error resetting conversation memory:', err);
        });
      }
    }, 30 * 1000); // Check every 30 seconds
  }

  stopMemoryWatcher() {
    if (this.memoryTimer) {
      clearInterval(this.memoryTimer);
      this.memoryTimer = null;
    }
  }

  /**
   * Fully restart Synapse to clear conversation memory.
   * Stops the entire session (AI + wake word) then starts fresh.
   */
  async resetConversationMemory(reason = 'unknown') {
    console.log(`🔄 Full Synapse restart due to: ${reason}`);
    try {
      // Temporarily store state so we know we were active
      const wasActive = this.state !== VAStates.INACTIVE;

      if (wasActive) {
        // Stop everything
        this.stopSession();

        // Brief pause to let things clean up
        await new Promise((resolve) => setTimeout(resolve, 200));

        // Start fresh
        await this.startSession();

        toast.info(`Comprehend Voice reset (${reason})`, { autoClose: 1500 });
      }

      this.lastInteractionAt = Date.now();
      this.hasBeenUsedSinceReset = false; // Fresh session, hasn't been used yet
    } catch (e) {
      console.error('Failed to restart Synapse:', e);
      toast.error('Failed to reset - please restart manually');
    }
  }

  silenceDetected() {
    // Stop the assistant when silence is detected
    this.stopConversation(
      'silence detected',
      'Silence detected, stopping assistant',
    );
  }

  stopListeningTool() {
    // Stop the assistant when AI requests it via tool call
    this.stopConversation('AI tool request', 'Assistant stopped listening');
  }

  /**
   * Register the appropriate mode switch tool based on current mode.
   * If in clinic mode, register activate_conversation_mode tool.
   * If in conversation mode, register activate_clinic_mode tool.
   */
  registerModeToolForCurrentMode() {
    if (this.mode === 'clinic') {
      // In clinic mode, show option to switch to conversation
      this.AI.addTools(createActivateConversationModeTool(this.switchModeTool));
    } else {
      // In conversation mode, show option to switch to clinic
      this.AI.addTools(createActivateClinicModeTool(this.switchModeTool));
    }
  }

  /**
   * Switch between clinic and conversation modes
   * @param {string} newMode - 'clinic' or 'conversation'
   */
  switchModeTool(newMode) {
    if (newMode !== 'clinic' && newMode !== 'conversation') {
      console.warn(`Invalid mode: ${newMode}`);
      return;
    }

    if (newMode === this.mode) {
      console.log(`Already in ${newMode} mode`);
      return;
    }

    const oldMode = this.mode;
    this.mode = newMode;
    const newTimeout = MODE_TIMEOUTS[newMode];

    // Update the silence timeout in the transcription service
    this.transcriptionService.setSilenceTimeout(newTimeout);

    // Swap the mode tools: remove the one we just used, add the one for the new mode
    if (oldMode === 'clinic') {
      this.AI.removeTools(['activate_conversation_mode']);
      this.AI.addTools(createActivateClinicModeTool(this.switchModeTool));
    } else {
      this.AI.removeTools(['activate_clinic_mode']);
      this.AI.addTools(createActivateConversationModeTool(this.switchModeTool));
    }

    // Notify the UI of the mode change
    if (this.onModeChange) {
      this.onModeChange(newMode);
    }

    const modeEmoji = newMode === 'clinic' ? '🏥' : '💬';
    const modeLabel =
      newMode === 'clinic' ? 'Clinic Mode' : 'Conversation Mode';
    toast.info(`${modeEmoji} Switched to ${modeLabel}`, { autoClose: 2000 });
    console.log(`Switched to ${newMode} mode (timeout: ${newTimeout}ms)`);
  }

  playDing(up = true) {
    // Reuse pre-created and unlocked audio elements (required for iOS Safari)
    const audio = up ? this.blipUpAudio : this.blipDownAudio;

    try {
      // Pause first if currently playing (prevents overlap issues)
      if (!audio.paused) {
        audio.pause();
      }
      // Reset to beginning
      audio.currentTime = 0;

      // Play with promise handling
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch((e) => {
          console.warn('Failed to play blip audio:', e.message);
        });
      }
    } catch (e) {
      console.warn('Error in playDing:', e.message);
    }
  }
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default VoiceAssistant;
